import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Clock, Loader2, Plus, Trash2, Users } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { ApiError } from "@/lib/api";
import { describeShift, shiftsApi, WEEKDAYS, type Shift, type ShiftDay } from "@/lib/shifts-api";

const DEFAULT_HOURS = { startTime: "08:00", endTime: "17:00" };

export default function Shifts() {
  const { toast } = useToast();
  const [rows, setRows] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<{
    code: string;
    name: string;
    gracePeriodMins: string;
    days: ShiftDay[];
  }>({ code: "", name: "", gracePeriodMins: "", days: [] });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await shiftsApi.list());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load shifts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleDay = (weekday: number) =>
    setForm((f) => {
      const has = f.days.some((d) => d.weekday === weekday);
      return {
        ...f,
        days: has
          ? f.days.filter((d) => d.weekday !== weekday)
          : [...f.days, { weekday, ...DEFAULT_HOURS }].sort((a, b) => a.weekday - b.weekday),
      };
    });

  const setDayHours = (weekday: number, key: "startTime" | "endTime", value: string) =>
    setForm((f) => ({
      ...f,
      days: f.days.map((d) => (d.weekday === weekday ? { ...d, [key]: value } : d)),
    }));

  const save = async () => {
    if (form.days.length === 0) {
      toast({
        variant: "destructive",
        title: "Pick at least one working day",
        description: "A shift with no days would mean nobody on it is ever expected at work.",
      });
      return;
    }
    setSaving(true);
    try {
      await shiftsApi.create({
        code: form.code.trim(),
        name: form.name.trim(),
        gracePeriodMins: form.gracePeriodMins === "" ? null : Number(form.gracePeriodMins),
        days: form.days,
      });
      setOpen(false);
      setForm({ code: "", name: "", gracePeriodMins: "", days: [] });
      await load();
      toast({ title: "Shift created" });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Could not create the shift",
        description: e instanceof ApiError ? e.message : "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: Shift) => {
    try {
      const { released } = await shiftsApi.remove(s.id);
      setRows((r) => r.filter((x) => x.id !== s.id));
      toast({
        title: `Removed ${s.name}`,
        description:
          released > 0
            ? `${released} ${released === 1 ? "employee" : "employees"} now follow their work location's hours.`
            : undefined,
      });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Could not remove it",
        description: e instanceof ApiError ? e.message : "Something went wrong",
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Shifts</h1>
          <p className="text-sm text-muted-foreground">
            Named working patterns. An employee on a shift follows its hours and its working
            week instead of their work location's.
          </p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" />
          New shift
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Couldn't load shifts</AlertTitle>
          <AlertDescription className="flex items-center gap-3">
            {error}
            <Button size="sm" variant="outline" onClick={() => void load()}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            {rows.length} {rows.length === 1 ? "shift" : "shifts"}
          </CardTitle>
        </CardHeader>
        <CardContent className="divide-y">
          {loading && <Skeleton className="h-40 w-full" />}
          {!loading && rows.length === 0 && (
            <p className="py-6 text-sm text-muted-foreground">
              No shifts yet. Without one, everybody follows their work location's hours —
              which is the right setup for a single-pattern organisation.
            </p>
          )}
          {!loading &&
            rows.map((s) => (
              <div key={s.id} className="flex items-start justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium">{s.name}</p>
                    <Badge variant="outline" className="text-[10px]">
                      {s.code}
                    </Badge>
                    {!s.isActive && <Badge variant="secondary">Inactive</Badge>}
                  </div>
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {describeShift(s.days)}
                  </p>
                  {s.gracePeriodMins != null && (
                    <p className="text-xs text-muted-foreground">
                      Grace period {s.gracePeriodMins} min (overrides the location)
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Users className="h-3 w-3" />
                    {s.employeeCount}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-destructive"
                    aria-label={`Remove ${s.name}`}
                    onClick={() => void remove(s)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New shift</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Code" hint="Short and unique, e.g. OFFICE.">
                <Input
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  placeholder="OFFICE"
                />
              </Field>
              <Field label="Name">
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Office hours"
                />
              </Field>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium">Working days and hours</p>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((w) => {
                  const on = form.days.some((d) => d.weekday === w.n);
                  return (
                    <button
                      key={w.n}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleDay(w.n)}
                      className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
                        on
                          ? "border-primary bg-primary/10 font-medium text-foreground"
                          : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {w.short}
                    </button>
                  );
                })}
              </div>
              {/* Hours are per day so a half-day Friday is expressible without a
                  second shift. A day left off is simply not worked. */}
              {form.days.length > 0 && (
                <div className="mt-2 space-y-1.5 rounded-md border p-2">
                  {form.days.map((d) => (
                    <div key={d.weekday} className="flex items-center gap-2">
                      <span className="w-10 text-xs text-muted-foreground">
                        {WEEKDAYS.find((w) => w.n === d.weekday)?.short}
                      </span>
                      <Input
                        type="time"
                        className="h-8"
                        value={d.startTime}
                        onChange={(e) => setDayHours(d.weekday, "startTime", e.target.value)}
                      />
                      <span className="text-xs text-muted-foreground">to</span>
                      <Input
                        type="time"
                        className="h-8"
                        value={d.endTime}
                        onChange={(e) => setDayHours(d.weekday, "endTime", e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Days not selected are not worked on this shift.
              </p>
            </div>

            <Field
              label="Grace period (minutes)"
              hint="Optional. Leave blank to use the work location's, then the organisation's."
            >
              <Input
                type="number"
                min={0}
                max={240}
                value={form.gracePeriodMins}
                onChange={(e) => setForm((f) => ({ ...f, gracePeriodMins: e.target.value }))}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void save()} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
